import { makeAutoObservable, observable } from "mobx";

const DEFAULT_PAGE_SIZE = 5;

const consolidatedKeyOf = (disclosureId, year) =>
  year != null ? `${disclosureId}:${year}` : disclosureId;

class ConsolidatedMetricDetailPanelState {
  pivot = null;
  rows = [];
  total = 0;
  headline = null;
  disclosure = null;
  ratioBreakdown = null;
  collapsedChildren = null;
  years = [];
  page = 1;
  pageSize = DEFAULT_PAGE_SIZE;
  search = "";
  statuses = [];
  drillOrgIds = [];
  loading = false;
  hasLoadedOnce = false;

  constructor(pivot) {
    this.pivot = pivot;
    this.loading = true;
    makeAutoObservable(this);
  }

  setResult = (data) => {
    this.rows = data?.rows ?? [];
    this.total = Number(data?.total ?? 0);
    this.headline = data?.headline ?? null;
    this.disclosure = data?.disclosure ?? null;
    this.ratioBreakdown = data?.ratioBreakdown ?? null;
    this.collapsedChildren = data?.collapsedChildren ?? null;
    this.years = data?.years ?? [];
    this.page = Number(data?.page ?? this.page);
    this.pageSize = Number(data?.pageSize ?? this.pageSize);
    this.hasLoadedOnce = true;
  };

  setLoading = (value) => {
    this.loading = Boolean(value);
  };

  setSearch = (value) => {
    this.search = value ?? "";
    this.page = 1;
  };

  setStatuses = (value) => {
    this.statuses = Array.isArray(value) ? value : [];
    this.page = 1;
  };

  setDrillOrgIds = (value) => {
    this.drillOrgIds = Array.isArray(value) ? value : [];
    this.page = 1;
  };

  setPageSize = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 1) return;
    this.pageSize = n;
    this.page = 1;
  };

  setPage = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 1) return;
    this.page = n;
  };
}

class DashboardConsolidatedMetricDetailStore {
  expandedKeys = observable.set();
  panels = observable.map();

  constructor() {
    makeAutoObservable(this, {
      expandedKeys: observable,
      panels: observable,
    });
  }

  toggle = ({ disclosureId, year = null }) => {
    const key = consolidatedKeyOf(disclosureId, year);
    if (this.expandedKeys.has(key)) {
      this.collapse(key);
    } else {
      this.expand({ disclosureId, year });
    }
  };

  expand = ({ disclosureId, year = null }) => {
    const key = consolidatedKeyOf(disclosureId, year);
    this.expandedKeys.add(key);
    this.panels.set(
      key,
      new ConsolidatedMetricDetailPanelState({ disclosureId, year }),
    );
  };

  collapse = (key) => {
    this.expandedKeys.delete(key);
    this.panels.delete(key);
  };

  collapseAll = () => {
    this.expandedKeys.clear();
    this.panels.clear();
  };

  isExpanded = (key) => this.expandedKeys.has(key);

  getPanel = (key) => this.panels.get(key);
}

export { consolidatedKeyOf };
export const dashboardConsolidatedMetricDetailStore =
  new DashboardConsolidatedMetricDetailStore();
